import { expectsReasoning } from '../../utils/reasoningDisplay';
import { finalizePrivateUpload } from '../../utils/privateStorage';
import { isPremiumChatLocked, PREMIUM_CHAT_LOCK_MESSAGE } from '../../utils/premiumChatAccess';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes } from 'firebase/storage';
import { auth, storage } from '../../firebase';
import uuid from 'react-native-uuid';
import { getEndpoint, isAnthropic, isGoogle } from '../../chatModels';
import { buildApiUrl } from '../../config/env';
import { useModels } from '../../contexts/ModelContext';
import { formatDuration } from '../../utils/formatUtils';
import { createChatOnServer, updateChatTopicOnServer } from '../../utils/chatApi';
import { serializeMessageForBackend } from '../../utils/messagePayload';
import { captureAnalyticsEvent } from '../../analytics/posthog';
import { applyAssistantStreamMeta } from './streamMeta';
import { consumeSseResponse } from '../../utils/sseStream';

const getAttachmentStorageInfo = (attachment) => {
  const mimeType = attachment.mimeType || '';
  switch (attachment.type) {
    case 'image':
      return { folder: 'chat_images', extension: 'jpg' };
    case 'pdf':
      return { folder: 'chat_pdfs', extension: 'pdf' };
    case 'audio':
      if (mimeType.includes('wav')) {
        return { folder: 'chat_audios', extension: 'wav' };
      }
      if (mimeType.includes('mpeg') || mimeType.includes('mp3')) {
        return { folder: 'chat_audios', extension: 'mp3' };
      }
      return { folder: 'chat_audios', extension: 'audio' };
    case 'video':
      if (mimeType.includes('quicktime')) {
        return { folder: 'chat_videos', extension: 'mov' };
      }
      if (mimeType.includes('webm')) {
        return { folder: 'chat_videos', extension: 'webm' };
      }
      if (mimeType.includes('mpeg')) {
        return { folder: 'chat_videos', extension: 'mpeg' };
      }
      return { folder: 'chat_videos', extension: 'mp4' };
    default:
      return { folder: 'chat_files', extension: 'bin' };
  }
};

const IMAGE_TOOL_NAME = 'image_generation';

const getChatProjectId = (chat = {}) => chat?.projectId || chat?.project_id || null;

const attachProjectId = (requestBody, projectId) => {
  if (requestBody && projectId) {
    requestBody.project_id = projectId;
  }
  return requestBody;
};

export const useDualMessageSending = ({
  selectedChat,
  primaryModel,
  secondaryModel,
  selectedModelName,
  onPrimaryMessageSent,
  onSecondaryMessageSent,
  primaryMessages,
  secondaryMessages,
  updateChatTitle,
  hasSubscription,
  input,
  setInput,
  setInputText,
  attachments = [],
  activeProjectId = null,
  clearFileState,
  isThinking,
  isDeepResearch,
  setIsDeepResearch,
  reasoningLevel
}) => {
  const { models, getModelByModelString, getMaxTokens } = useModels();
  const lastUpdateRef = useRef(0);
  const topicGeneratedChatsRef = useRef(new Set());
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const canSendRef = useRef(null);
  canSendRef.current = () => {
    if (isPremiumChatLocked(selectedChat, [...primaryMessages, ...secondaryMessages], models, hasSubscription, [primaryModel, secondaryModel])) {
      setError(PREMIUM_CHAT_LOCK_MESSAGE);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (selectedChat?.id && selectedChat?.topic && selectedChat.topic !== 'Untitled Chat') {
      topicGeneratedChatsRef.current.add(selectedChat.id);
    }
  }, [selectedChat]);

  const generateAndUpdateTopic = useCallback(async (responseText, chatId) => {
    if (!chatId || topicGeneratedChatsRef.current.has(chatId)) {
      return;
    }

    const lastUserMessage = primaryMessages[primaryMessages.length - 2]?.text || '';
    const lastAssistantMessage = responseText;

    const topicPrompt = `
      Based on the following conversation, generate a concise topic of 4-5 words maximum and 30 letters or less:
      User: ${lastUserMessage}
      AI: ${lastAssistantMessage}
      Topic:
    `;

    const authToken = await auth.currentUser.getIdToken();
    const topicResponse = await fetch(
      buildApiUrl('/generate-topic'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ prompt: topicPrompt }),
      }
    );

    const result = await topicResponse.json();
    const generatedTopic = result.topic || 'Untitled Chat';

    try {
      await updateChatTopicOnServer(chatId, generatedTopic);
      topicGeneratedChatsRef.current.add(chatId);
      updateChatTitle(generatedTopic);
    } catch (updateError) {
      console.error('Error updating chat topic:', updateError);
    }
  }, [primaryMessages, updateChatTitle]);

  const sendMessageToModel = useCallback(async (
    modelString,
    conversationHistory,
    chatId,
    onMessageSent,
    messageId,
    {
      backendUserMessagePayload,
      shouldPersistUserMessage = true,
      assistantMetadata = {},
      projectId = null,
    } = {}
  ) => {
    const modelDetails = getModelByModelString(modelString);

    let accumulatedResponse = '';
    let accumulatedReasoning = '';
    let lastCitations = [];
    let isWebSearching = false;
    const maxTokens = getMaxTokens(modelString);
    const metadata = {
      modelUsed: assistantMetadata?.modelUsed || modelString,
      ...(assistantMetadata?.isDualMode ? { isDualMode: true, dualModeType: assistantMetadata.dualModeType } : {}),
    };

    captureAnalyticsEvent('model_used', {
      mode: metadata.isDualMode ? 'dual' : 'single',
      chat_id: chatId || null,
      model_string: modelString,
      model_name: metadata.modelUsed,
      dual_mode_type: metadata.dualModeType || null,
      is_thinking: Boolean(isThinking),
      is_deep_research: Boolean(isDeepResearch),
    });

    const assistantMessage = {
      id: messageId,
      text: '',
      reasoning_text: '',
        reasoning_pending: expectsReasoning(modelDetails, { isThinking, isDeepResearch, reasoningLevel }),
      reasoning_is_summary: false,
      reasoning_time: '',
      role: 'assistant',
      streaming: true,
      isWebSearching: false,
      modelUsed: metadata.modelUsed,
    };

    if (metadata.isDualMode) {
      assistantMessage.isDualMode = true;
      assistantMessage.dualModeType = metadata.dualModeType || null;
    }

    const updateMessage = (text, force = false) => {
      const now = Date.now();
      if (force || now - lastUpdateRef.current >= 50) {
        const updatedMessage = {
          ...assistantMessage,
          text: text.trim()
        };
        onMessageSent(updatedMessage, chatId);
        lastUpdateRef.current = now;
        assistantMessage.text = text.trim();
      }
    };

      let reasoningStartTime = null;
      let reasoningEndTime = null;

      const requestBody = {
        messages: conversationHistory,
        model: modelString,
        max_tokens: maxTokens,
        user_id: auth.currentUser.uid,
        isReasoning: modelDetails?.isReasoning || false,
        isThinking: Boolean(isThinking),
        isDeepResearch: Boolean(isDeepResearch),
        reasoningLevel: reasoningLevel || 'Auto',
      };

      if (chatId && backendUserMessagePayload) {
        requestBody.chat_id = chatId;
        attachProjectId(requestBody, projectId);
        requestBody.user_message = backendUserMessagePayload;
        requestBody.save_user_message = shouldPersistUserMessage !== false;
        requestBody.assistant_message_id = messageId;
        requestBody.assistant_message_metadata = metadata;
      }
      onMessageSent({ ...assistantMessage }, chatId);

      const authToken = await auth.currentUser.getIdToken();
      const baseEndpoint = getEndpoint(modelDetails, hasSubscription);
      const finalEndpoint = isThinking && modelDetails?.thinking && isAnthropic(modelDetails)
        ? `${baseEndpoint}-thinking`
        : baseEndpoint;

      const response = await fetch(finalEndpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = 'Something went wrong. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }

        assistantMessage.streaming = false;
        assistantMessage.text = errorMessage;
        onMessageSent({ ...assistantMessage }, chatId);

        captureAnalyticsEvent('message_failed', {
          mode: metadata.isDualMode ? 'dual' : 'single',
          chat_id: chatId || null,
          model_string: modelString,
          model_name: metadata.modelUsed,
          dual_mode_type: metadata.dualModeType || null,
          reason: errorMessage,
        });
        throw new Error(errorMessage);
      }

      await consumeSseResponse(response, (parsedData) => {
              const {
                content,
                reasoning_content,
                reasoning_summary,
                citations,
                meta,
                error: streamError,
                image_url: imageUrlFromEvent,
                imageUrl: imageUrlFromEventAlt,
                openai_response_id: openaiResponseId,
                attachment: generatedAttachment,
                tool_error: toolError,
                tool_name: toolName,
              } = parsedData;

              if (streamError) {
                if (toolError || toolName === IMAGE_TOOL_NAME) {
                  assistantMessage.streaming = false;
                  assistantMessage.isImageGenerating = false;
                  assistantMessage.imageGenerationStatus = 'error';
                  assistantMessage.toolError = true;
                  assistantMessage.toolName = IMAGE_TOOL_NAME;
                  assistantMessage.text = streamError;
                  onMessageSent({ ...assistantMessage }, chatId);
                }
                captureAnalyticsEvent('message_failed', {
                  mode: metadata.isDualMode ? 'dual' : 'single',
                  chat_id: chatId || null,
                  model_string: modelString,
                  model_name: metadata.modelUsed,
                  dual_mode_type: metadata.dualModeType || null,
                  reason: streamError,
                });
                const streamErrorObject = new Error(streamError);
                streamErrorObject.isStreamError = true;
                throw streamErrorObject;
              }

              if (meta) {
                isWebSearching = applyAssistantStreamMeta({
                  meta,
                  assistantMessage,
                  chatId,
                  onMessageSent,
                  isWebSearching,
                  imageToolName: IMAGE_TOOL_NAME,
                });
              }

              if (openaiResponseId) {
                assistantMessage.openaiResponseId = openaiResponseId;
              }

              const generatedImageUrl = imageUrlFromEvent || imageUrlFromEventAlt;
              if (generatedImageUrl) {
                const imageMarkdown = `\n\n![Generated image](<${generatedImageUrl}>)\n\n`;
                if (!accumulatedResponse.includes(generatedImageUrl)) {
                  accumulatedResponse += imageMarkdown;
                  updateMessage(accumulatedResponse);
                }
                assistantMessage.isImageGenerating = false;
                assistantMessage.imageGenerationStatus = 'completed';
                assistantMessage.toolError = false;
                assistantMessage.toolName = IMAGE_TOOL_NAME;
                assistantMessage.attachments = generatedAttachment
                  ? [generatedAttachment]
                  : [
                      {
                        id: `${messageId}-generated-image`,
                        type: 'image',
                        name: 'Generated Image',
                        url: generatedImageUrl,
                        previewUrl: generatedImageUrl,
                      },
                    ];
              }

              if (citations && Array.isArray(citations)) {
                lastCitations = citations;
              }

              if (reasoning_content && !reasoningStartTime) {
                reasoningStartTime = Date.now();
              }
              if (reasoning_content) {
                accumulatedReasoning += reasoning_content;
                assistantMessage.reasoning_text = accumulatedReasoning;
                if (reasoning_summary) {
                  assistantMessage.reasoning_is_summary = true;
                }
                updateMessage(accumulatedResponse, true);
              }

              if (content) {
                if (reasoningStartTime && !reasoningEndTime) {
                  reasoningEndTime = Date.now();
                }
                accumulatedResponse += content;
                updateMessage(accumulatedResponse);
              }
              return true;
      });

      let reasoningTime = null;
      if (reasoningStartTime) {
        reasoningTime = (reasoningEndTime || Date.now()) - reasoningStartTime;
      }
      if (reasoningTime != null) {
        assistantMessage.reasoning_time = formatDuration(reasoningTime);
      }

      assistantMessage.streaming = false;
      assistantMessage.text = accumulatedResponse.trim();
      assistantMessage.reasoning_text = accumulatedReasoning.trim();


      if (lastCitations.length > 0) {
        assistantMessage.citations = lastCitations;
      }

      onMessageSent({ ...assistantMessage }, chatId);
      captureAnalyticsEvent('message_completed', {
        mode: metadata.isDualMode ? 'dual' : 'single',
        chat_id: chatId || null,
        model_string: modelString,
        model_name: metadata.modelUsed,
        dual_mode_type: metadata.dualModeType || null,
        response_type: 'text',
        response_char_count: assistantMessage.text.length,
        citation_count: lastCitations.length,
        reasoning_duration_ms: reasoningTime,
      });
      return assistantMessage;
    
  }, [
    getModelByModelString,
    getMaxTokens,
    hasSubscription,
    isDeepResearch,
    isThinking,
    reasoningLevel,
  ]);

  const handleDualSendMessage = useCallback(async () => {
    if (!canSendRef.current()) return;
    const projectIdForRequest = getChatProjectId(selectedChat) || activeProjectId || null;
    const messageText = input.trim();
    if (!messageText.trim() && attachments.length === 0) return;
    if (!auth.currentUser?.uid) {
      navigate('/welcome');
      return;
    }

    // Clear any previous error
    setError(null);

    captureAnalyticsEvent('message_send_requested', {
      mode: 'dual',
      chat_id: selectedChat?.id || null,
      primary_model_string: primaryModel,
      primary_model_name: getModelByModelString(primaryModel)?.name || primaryModel,
      secondary_model_string: secondaryModel,
      secondary_model_name: getModelByModelString(secondaryModel)?.name || secondaryModel,
      attachment_count: attachments.length,
      is_thinking: Boolean(isThinking),
      is_deep_research: Boolean(isDeepResearch),
      is_retry: false,
      is_edit: false,
    });

    const normalizedAttachments = attachments.map((attachment) => ({
      id: attachment.id,
      type: attachment.type,
      name: attachment.name,
      mimeType: attachment.mimeType,
      size: attachment.size,
      url: attachment.previewUrl || attachment.url,
      previewUrl: attachment.previewUrl || attachment.url,
      isLocal: true,
      uploading: true,
    }));

    const userMessage = {
      id: uuid.v4(),
      text: messageText,
      role: 'user',
      streaming: false,
      pending: true,
      isDualMode: true,
      attachments: normalizedAttachments,
    };

    let chatId = selectedChat?.id;
    let backendUserMessagePayload = null;

    // Kick off async work so we can update the UI immediately
    const chatPromise = chatId
      ? Promise.resolve({ chatId })
      : createChatOnServer({
          modelName: selectedModelName || 'Dual Model Chat',
          multiModel: true,
          projectId: projectIdForRequest,
        });

    const shouldProcessForGemini = [primaryModel, secondaryModel]
      .map((modelString) => getModelByModelString(modelString))
      .some(isGoogle);
    const geminiAuthTokenPromise = shouldProcessForGemini && attachments.length > 0
      ? auth.currentUser.getIdToken()
      : null;
    const attachmentUploadPromise = (attachments.length > 0
      ? Promise.all(attachments.map(async (attachment) => {
          if (!attachment?.file) {
            console.warn('Skipping attachment without file data', attachment);
            return userMessage.attachments.find((item) => item.id === attachment?.id);
          }

          const { folder, extension } = getAttachmentStorageInfo(attachment);
          const storagePath = `${folder}/${auth.currentUser.uid}/${Date.now()}_${attachment.id}.${extension}`;
          const fileRef = ref(storage, storagePath);

          await uploadBytes(fileRef, attachment.file);
          const { url: downloadUrl } = await finalizePrivateUpload(storagePath);

          let uploadedAttachment = {
            ...userMessage.attachments.find((item) => item.id === attachment.id),
            storagePath,
            url: downloadUrl,
            previewUrl: downloadUrl,
            isLocal: false,
            uploading: false,
            mimeType: attachment.mimeType,
          };

          if (geminiAuthTokenPromise) {
            try {
              const authToken = await geminiAuthTokenPromise;
              const response = await fetch(buildApiUrl('/gemini-file-upload'), {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  fileUrl: downloadUrl,
                  fileType: attachment.type
                })
              });

              if (response.ok) {
                const { fileUri, mimeType } = await response.json();
                uploadedAttachment = { ...uploadedAttachment, fileUri, mimeType };
              } else {
                console.error('Failed to process file');
              }
            } catch (error) {
              console.error('Error processing file:', error);
            }
          }

          return uploadedAttachment;
        }))
      : Promise.resolve(null)
    ).then(
      (value) => ({ value, error: null }),
      (error) => ({ value: null, error })
    );

    // Optimistic UI update for existing chats
    if (chatId) {
      onPrimaryMessageSent({ ...userMessage }, chatId);
      onSecondaryMessageSent({ ...userMessage }, chatId);
      setInput('');
      if (setInputText) {
        setInputText('');
      }
      clearFileState();
      if (isDeepResearch) {
        setIsDeepResearch(false);
      }
    }

    try {
      const chatResult = await chatPromise;
      chatId = chatResult.chatId;

      // For brand new chats, render the message as soon as the id arrives
      if (!selectedChat?.id) {
        captureAnalyticsEvent('chat_created', {
          chat_id: chatId,
          mode: 'dual',
          primary_model_string: primaryModel,
          primary_model_name: getModelByModelString(primaryModel)?.name || primaryModel,
          secondary_model_string: secondaryModel,
          secondary_model_name: getModelByModelString(secondaryModel)?.name || secondaryModel,
          source: 'first_message',
        });

        onPrimaryMessageSent({ ...userMessage }, chatId);
        onSecondaryMessageSent({ ...userMessage }, chatId);
        topicGeneratedChatsRef.current.delete(chatId);
        updateChatTitle('Untitled Chat');
        setInput('');
        if (setInputText) {
          setInputText('');
        }
        clearFileState();
        if (isDeepResearch) {
          setIsDeepResearch(false);
        }
      }

      // Handle file uploads (supports multiple attachments)
      const { value: uploadedAttachments, error: uploadError } =
        await attachmentUploadPromise;
      if (uploadError) throw uploadError;
      if (uploadedAttachments) {
        userMessage.attachments = uploadedAttachments.filter(Boolean);
        const updatedMessage = {
          ...userMessage,
          attachments: userMessage.attachments.map((item) => ({ ...item })),
        };
        onPrimaryMessageSent(updatedMessage, chatId);
        onSecondaryMessageSent(updatedMessage, chatId);
      }

      backendUserMessagePayload = serializeMessageForBackend(userMessage);
      userMessage.pending = false;
      onPrimaryMessageSent({ ...userMessage }, chatId);
      onSecondaryMessageSent({ ...userMessage }, chatId);

      // Send to both models simultaneously
      const primaryMessageId = uuid.v4();
      const secondaryMessageId = uuid.v4();
      
      const conversationHistory = [...primaryMessages, userMessage];

      const [primaryResponse] = await Promise.all([
        sendMessageToModel(primaryModel, conversationHistory, chatId, onPrimaryMessageSent, primaryMessageId, {
          backendUserMessagePayload,
          shouldPersistUserMessage: true,
          projectId: projectIdForRequest,
          assistantMetadata: {
            modelUsed: getModelByModelString(primaryModel)?.name || primaryModel,
            isDualMode: true,
            dualModeType: 'primary',
          },
        }),
        sendMessageToModel(secondaryModel, conversationHistory, chatId, onSecondaryMessageSent, secondaryMessageId, {
          backendUserMessagePayload,
          shouldPersistUserMessage: false,
          projectId: projectIdForRequest,
          assistantMetadata: {
            modelUsed: getModelByModelString(secondaryModel)?.name || secondaryModel,
            isDualMode: true,
            dualModeType: 'secondary',
          },
        }),
      ]);
      
      // Generate topic based on primary response
      await generateAndUpdateTopic(primaryResponse.text, chatId);

    } catch (error) {
      console.error('Error sending dual message:', error);
      setError(error.message || 'Something went wrong. Please try again.');
      captureAnalyticsEvent('message_failed', {
        mode: 'dual',
        chat_id: chatId || null,
        primary_model_string: primaryModel,
        secondary_model_string: secondaryModel,
        reason: error.message || 'send_dual_message_exception',
      });
      if (chatId) {
        const failedMessage = { ...userMessage, pending: false, error: true };
        onPrimaryMessageSent(failedMessage, chatId);
        onSecondaryMessageSent(failedMessage, chatId);
      }
    }
  }, [
    input,
    attachments,
    activeProjectId,
    selectedChat,
    primaryModel,
    secondaryModel,
    selectedModelName,
    onPrimaryMessageSent,
    onSecondaryMessageSent,
    primaryMessages,
    navigate,
    isThinking,
    isDeepResearch,
    clearFileState,
    setIsDeepResearch,
    updateChatTitle,
    generateAndUpdateTopic,
    getModelByModelString,
    sendMessageToModel,
    setInput,
    setInputText,
  ]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const retryMessage = useCallback(async () => {
    if (!canSendRef.current()) return;
    if (!auth.currentUser?.uid) {
      navigate('/welcome');
      return;
    }

    // Find the last user message from the primary conversation history
    const lastUserMessage = primaryMessages.slice().reverse().find(msg => msg.role === 'user');
    
    if (!lastUserMessage) {
      console.warn('No user message to retry');
      return;
    }

    // Clear error 
    setError(null);

    captureAnalyticsEvent('message_retry_requested', {
      mode: 'dual',
      chat_id: selectedChat?.id || null,
      primary_model_string: primaryModel,
      secondary_model_string: secondaryModel,
    });

    // Set the input to the last user message text
    setInput(lastUserMessage.text || '');
    
    // Also set the inputText if the setter is available
    if (setInputText) {
      setInputText(lastUserMessage.text || '');
    }
    
    // Wait a moment for the input to be set, then call handleDualSendMessage
    setTimeout(() => {
      handleDualSendMessage();
    }, 50);
  }, [primaryMessages, navigate, setError, setInput, setInputText, handleDualSendMessage, selectedChat, primaryModel, secondaryModel]);

  return {
    handleDualSendMessage,
    error,
    clearError,
    retryMessage
  };
};
